import type { Column } from "@shared/types/data-table"
import type { PosInventoryItemWithStatus } from "@shared/data/mockPosInventory"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createTruncatedColumn,
  createDateColumn,
  createStatusColumn,
  createTextColumn,
} from "./column-factories"
import { CurrencyCell, TextCell } from "../cells"
import { ITEM_TYPE_LABELS } from "@shared/utils/pos-inventory-utils"
import type { InventoryItemType } from "@/types/pos"

export const POS_INVENTORY_COLUMNS: Record<string, Column<PosInventoryItemWithStatus>> = {
  name: createTruncatedColumn<PosInventoryItemWithStatus>(
    "name",
    "Item Name",
    200,
    (item) => item.name,
    "font-medium"
  ),
  sku: createTruncatedColumn<PosInventoryItemWithStatus>(
    "sku",
    "SKU",
    110,
    (item) => item.sku,
    "font-mono text-sm"
  ),
  category: createTextColumn<PosInventoryItemWithStatus>("category", "Category", 120),
  type: createTruncatedColumn<PosInventoryItemWithStatus>(
    "type",
    "Type",
    120,
    (item) => ITEM_TYPE_LABELS[item.type as InventoryItemType] || item.type
  ),
  stock: {
    key: "currentStock",
    header: "Stock",
    width: 100,
    render: (item: PosInventoryItemWithStatus) => (
      <TextCell
        value={`${item.currentStock} ${item.unit}`}
        className="tabular-nums text-sm"
      />
    ),
  },
  parLevel: {
    key: "parLevel",
    header: "Par Level",
    width: 100,
    render: (item: PosInventoryItemWithStatus) => (
      <TextCell
        value={`${item.parLevel} ${item.unit}`}
        className="tabular-nums text-sm text-muted-foreground"
      />
    ),
  },
  status: createStatusColumn<PosInventoryItemWithStatus>("pos-inventory"),
  unitCost: {
    key: "unitCost",
    header: "Unit Cost",
    width: 100,
    render: (item: PosInventoryItemWithStatus) => (
      <CurrencyCell
        amount={item.unitCost}
        currency={item.currency}
        align="left"
        className="text-sm min-w-[68px]"
      />
    ),
  },
  totalValue: {
    key: "totalValue",
    header: "Total Value",
    width: 120,
    render: (item: PosInventoryItemWithStatus) => (
      <CurrencyCell
        amount={item.currentStock * item.unitCost}
        currency={item.currency}
        align="left"
        className="text-sm min-w-[68px]"
      />
    ),
  },
  reorderPoint: {
    key: "reorderPoint",
    header: "Reorder At",
    width: 100,
    render: (item: PosInventoryItemWithStatus) => (
      <TextCell
        value={`${item.reorderPoint} ${item.unit}`}
        className="tabular-nums text-sm text-muted-foreground"
      />
    ),
  },
  reorderQuantity: {
    key: "reorderQuantity",
    header: "Reorder Qty",
    width: 100,
    render: (item: PosInventoryItemWithStatus) => (
      <TextCell
        value={`${item.reorderQuantity} ${item.unit}`}
        className="tabular-nums text-sm text-muted-foreground"
      />
    ),
  },
  supplier: createTruncatedColumn<PosInventoryItemWithStatus>(
    "supplierId" as keyof PosInventoryItemWithStatus,
    "Supplier",
    140,
    (item) => item.supplierId || "—"
  ),
  expiryDate: createDateColumn<PosInventoryItemWithStatus>("expiryDate" as keyof PosInventoryItemWithStatus, "Expiry", 120, false),
  updatedAt: createDateColumn<PosInventoryItemWithStatus>("updatedAt", "Last Updated", 130),
}

export const POS_INVENTORY_STATUS_CONFIG = {
  all: ['name', 'sku', 'category', 'type', 'stock', 'parLevel', 'status', 'unitCost', 'totalValue', 'updatedAt'],
  critical: ['name', 'sku', 'stock', 'parLevel', 'reorderPoint', 'status', 'reorderQuantity', 'supplier', 'unitCost'],
  low: ['name', 'sku', 'stock', 'parLevel', 'reorderPoint', 'status', 'unitCost', 'totalValue', 'expiryDate'],
  good: ['name', 'sku', 'category', 'type', 'stock', 'parLevel', 'status', 'unitCost', 'totalValue'],
  overstocked: ['name', 'sku', 'stock', 'parLevel', 'status', 'unitCost', 'totalValue', 'category'],
} as const

export const getPosInventoryColumns = (status: string = 'all'): Column<PosInventoryItemWithStatus>[] => {
  const keys = POS_INVENTORY_STATUS_CONFIG[status as keyof typeof POS_INVENTORY_STATUS_CONFIG] || POS_INVENTORY_STATUS_CONFIG.all
  return keys.map(key => POS_INVENTORY_COLUMNS[key]).filter(Boolean)
}

export const getPosInventoryColumnConfig = (): ColumnManagementConfig[] => {
  return Object.entries(POS_INVENTORY_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: (POS_INVENTORY_STATUS_CONFIG.all as readonly string[]).includes(key),
    required: ['name', 'sku'].includes(key),
    order: index,
  }))
}
